/**
 * Setting option registry — SSOT cùng `settingComponentMap.ts`.
 *
 * Khi thêm setting mới:
 * 1. Thêm data-comp vào `settingComponentMap.ts` (action, scrollTo, parentDataComp?, needsDataComp?)
 * 2. Thêm scrollTo vào SettingOptionDocs với docs phù hợp
 * 3. Coverage/test tự pick up — không sửa allowlist rời trong test.
 */

export const THEME_DOCUMENT_TYPES = [
  'invoice',
  'order',
  'packing_slip',
  'draft_order',
  'refund',
] as const

export type ThemeDocumentType = (typeof THEME_DOCUMENT_TYPES)[number]

export type SettingComponentConfig = {
  action: string
  scrollTo: string
  /** false = Form có setting nhưng Liquid không cần data-comp riêng (BACKEND/STYLES). */
  needsDataComp?: boolean
  /** Parent data-comp(s) — nested OK nếu parent có trong Liquid. */
  parentDataComp?: string | readonly string[]
}

/** scrollTo → document types hiện trên Form. */
export const SettingOptionDocs: Record<string, readonly ThemeDocumentType[]> = {
  footer_note: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_barcode: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_barcode_number: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_address: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_apartment: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_city: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_company: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_company_id: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_country: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_email: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_heading: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_location_tax_id: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_name: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_phone: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_billing_zip: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_custom_number: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_shopify_order_number: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_discount: ['invoice', 'order', 'draft_order'],
  is_footer_facebook: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_footer_instagram: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_footer_pinterest: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_footer_status: ['packing_slip'],
  is_footer_twitter: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_footer_website: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_footer_youtube: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_issue_date: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_item_barcode: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_item_checkbox: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_item_compare_at_price: ['invoice', 'order', 'refund'],
  is_item_discount: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_item_price: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_item_properties: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_item_properties_order: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_item_quantity: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_item_sku: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_item_subtotal_ex_tax: ['invoice', 'order'],
  is_item_subtotal_inc_discount: ['invoice', 'order', 'draft_order'], // needsDataComp:false trên map
  is_item_tax: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_item_thumbnail: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_item_total: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_item_unit_price_excl_tax: ['invoice', 'order', 'refund'], // needsDataComp:false trên map
  is_item_vendor: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_logo: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_net_shipping: ['invoice', 'order'],
  is_number: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  is_online_version: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_payment_method: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_price_after_discount: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_refund_date: ['refund'],
  is_refund_no: ['refund'],
  is_shipping: ['invoice', 'order', 'draft_order'],
  is_shipping_address: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_apartment: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_city: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_company: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_company_id: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_country: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_email: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_heading: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_location_tax_id: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_method: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_name: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_phone: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_shipping_zip: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_show_barcode_value: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_show_heading_overview: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_show_tax_name: ['invoice', 'order'],
  is_show_tax_percent: ['invoice', 'order'],
  is_subtotal: ['invoice', 'order', 'draft_order', 'refund'],
  is_subtotal_ex_tax: ['invoice', 'order'],
  is_subtotal_inc_discount: ['invoice', 'order', 'draft_order'], // needsDataComp:false trên map
  is_supplier_address: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_supplier_apartment: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_supplier_email: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_supplier_heading: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_supplier_name: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_supplier_phone: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_supplier_registered_number: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_supplier_vat_number: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  is_tax: ['invoice', 'order', 'draft_order', 'refund'],
  is_tax_number: ['invoice', 'order', 'packing_slip', 'refund'],
  is_total: ['invoice', 'order', 'draft_order'],
  is_total_outstanding: ['invoice', 'order', 'draft_order'],
  is_total_paid: ['invoice', 'order'],
  is_total_refund: ['invoice', 'order', 'refund'],
  is_total_shipping: ['refund'],
  is_total_shipping_tax: ['refund'],
  items_note: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  show_bundle: ['draft_order'],
  show_customer_metafields: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_delivered_date: ['packing_slip'],
  show_discount_code: ['invoice', 'order', 'draft_order'],
  show_due_date: ['invoice', 'order', 'draft_order'],
  show_duties: ['invoice', 'order', 'refund'],
  show_estimated_date: ['packing_slip'],
  show_footer_term: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_fulfill_address: ['packing_slip'],
  show_fulfill_apartment: ['packing_slip'],
  show_fulfill_city: ['packing_slip'],
  show_fulfill_company: ['packing_slip'],
  show_fulfill_country: ['packing_slip'],
  show_fulfill_email: ['packing_slip'],
  show_fulfill_heading: ['packing_slip'],
  show_fulfill_name: ['packing_slip'],
  show_fulfill_phone: ['packing_slip'],
  show_fulfill_zip: ['packing_slip'],
  show_gift_card_code: ['invoice', 'order', 'refund'],
  show_hs_code: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  show_in_stock: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  show_invoice_extra_options: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_item_barcode: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_item_description: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_item_metafields: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_item_product_category: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_item_product_type: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_item_product_weight: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_order_metafields: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_order_note: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_order_status_url: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_payment_terms: ['invoice', 'order', 'packing_slip', 'draft_order'],
  show_po_number: ['invoice', 'order', 'packing_slip', 'draft_order'],
  show_product_title: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_shipping_method: ['invoice', 'order', 'draft_order'],
  show_supplier_extra: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_tags: ['invoice', 'order', 'packing_slip', 'draft_order', 'refund'],
  show_tax_line: ['invoice', 'order', 'draft_order'],
  show_thank_you_note: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_timeline: ['invoice', 'order', 'packing_slip'],
  show_timeline_event_date: ['invoice', 'order', 'packing_slip'],
  show_timeline_event_location: ['packing_slip'],
  show_timeline_heading: ['invoice', 'order', 'packing_slip'],
  show_total_qty: ['packing_slip'],
  show_total_weight: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  show_tracking_company: ['packing_slip'],
  show_tracking_heading: ['packing_slip'],
  show_tracking_number: ['packing_slip'],
  show_tracking_url: ['packing_slip'],
  show_used_gift_cards: ['invoice', 'order', 'refund'],
  show_variant_metafields: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  supplier_show_city: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  supplier_show_country: [
    'invoice',
    'order',
    'packing_slip',
    'draft_order',
    'refund',
  ],
  timeline_separate_page: ['invoice', 'order', 'packing_slip'],
}

/** Setting không yêu cầu data-comp trong Liquid (fallback nếu map chưa ghi needsDataComp). */
export const SETTINGS_WITHOUT_DATA_COMP = new Set<string>([
  'is_item_subtotal_inc_discount',
  'is_item_unit_price_excl_tax',
  'is_item_weight_unit',
  'is_subtotal_inc_discount',
  'line_item_metafield_translate',
  'line_item_padding',
  'line_item_translate',
  'template_currency',
])

export function settingNeedsDataComp(
  settingKey: string,
  cfg?: Pick<SettingComponentConfig, 'needsDataComp'>,
): boolean {
  if (cfg?.needsDataComp === false) return false
  if (cfg?.needsDataComp === true) return true
  return !SETTINGS_WITHOUT_DATA_COMP.has(settingKey)
}

export function docsForSettingOption(
  settingKey: string,
): readonly ThemeDocumentType[] {
  return SettingOptionDocs[settingKey] ?? []
}

export function settingAllowedOnDoc(
  settingKey: string,
  doc: ThemeDocumentType,
  cfg?: Pick<SettingComponentConfig, 'needsDataComp'>,
): boolean {
  if (!settingNeedsDataComp(settingKey, cfg)) return false
  return docsForSettingOption(settingKey).includes(doc)
}

/** Build allowlist theo doc từ SettingOptionDocs (cho test/debug). */
export function buildSettingOptionsByDoc(): Record<
  ThemeDocumentType,
  string[]
> {
  const out = Object.fromEntries(
    THEME_DOCUMENT_TYPES.map(d => [d, [] as string[]]),
  ) as Record<ThemeDocumentType, string[]>
  for (const [settingKey, docs] of Object.entries(SettingOptionDocs)) {
    if (!settingNeedsDataComp(settingKey)) continue
    for (const doc of docs) {
      out[doc].push(settingKey)
    }
  }
  for (const doc of THEME_DOCUMENT_TYPES) {
    out[doc].sort()
  }
  return out
}

export function getParentDataComps(
  _dataComp: string,
  cfg?: Pick<SettingComponentConfig, 'parentDataComp'>,
): readonly string[] {
  const parent = cfg?.parentDataComp
  if (!parent) return []
  return typeof parent === 'string' ? [parent] : parent
}
